import { useState } from "react";
import ReactJson from "react-json-view";
import EobClaimDetailView from "./EobClaimDetailView";
import SimpleTable from "../../../shared/SimpleTable";
import "./ClaimDetails.scss";

const asArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

const dash = (value) => (value == null || value === "" ? "—" : String(value));

const formatDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const statusTone = (status) => {
    const value = String(status || "").toLowerCase();
    if (["final", "paid", "complete", "fn"].includes(value)) return "ok";
    if (["denied", "rejected", "voided", "void", "rj", "dn"].includes(value)) return "bad";
    if (
        value.includes("review")
        || value.includes("repair")
        || value.includes("repric")
        || value.includes("need")
    ) return "warn";
    return "neutral";
};

const Badge = ({ children, tone = "neutral" }) => (
    <span className={`claim-detail__badge claim-detail__badge--${tone}`}>{children}</span>
);

const Meta = ({ label, value }) => {
    if (value == null || value === "") return null;
    return (
        <div className="claim-detail__meta-item">
            <div className="claim-detail__meta-label">{label}</div>
            <div className="claim-detail__meta-value">{value}</div>
        </div>
    );
};

const getClassicHccId = (item) =>
    item?.eobClaimDetail?.claim?.hccId
    || item?.eobClaimDetail?.claim?.hccID
    || item?.claimHccId;

const V4_LINE_COLUMNS = [
    { label: "#", attribute: "hccClaimLineNumber" },
    { label: "Status", attribute: "lineStatus" },
    { label: "CPT", attribute: "procedureCode" },
    { label: "From", attribute: "serviceFromDate" },
    { label: "To", attribute: "serviceToDate" },
    { label: "Practitioner", attribute: "renderingPractitionerHccId" },
];

const V4Fallback = ({ item }) => {
    const header = item?.header || {};
    const member = header.memberInfo || {};
    const submittedMember = header.submittedMemberInformation || {};
    const exceptions = asArray(header.activityExceptions?.exception);
    const lines = asArray(item?.lines?.lineDetails);

    return (
        <>
            <div className="claim-detail__card">
                <p className="claim-detail__card-title">Member</p>
                <div className="claim-detail__meta">
                    <Meta label="Name" value={member.memberName || submittedMember.memberName} />
                    <Meta label="Member ID" value={member.memberId || header.submittedSubscriberId} />
                    <Meta label="DOB" value={member.birthDate || submittedMember.birthdate} />
                    <Meta label="Gender" value={member.gender} />
                    <Meta label="Received" value={header.dateReceived} />
                </div>
            </div>

            {exceptions.length > 0 && (
                <div className="claim-detail__card">
                    <p className="claim-detail__card-title">Exceptions ({exceptions.length})</p>
                    {exceptions.map((exception, index) => (
                        <div key={index} className="claim-detail__line">
                            <p className="danger" style={{ fontWeight: 600, marginBottom: ".2rem" }}>
                                {exception?.exceptionMessageCode?.messageDescription || exception?.exceptionType}
                            </p>
                            <p className="sub-title">
                                {[exception?.exceptionType, exception?.enforcementLevel, exception?.exceptionMessageCode?.policyName]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            <div className="claim-detail__card">
                <p className="claim-detail__card-title">Service lines ({lines.length})</p>
                {lines.length > 0 ? (
                    <>
                        <SimpleTable columns={V4_LINE_COLUMNS} disableHeaderFormatting rows={lines} />
                        {lines.map((line) => {
                            const lineExceptions = asArray(line.lineActivityExceptions?.exception);
                            const reviews = asArray(line.lineReviewMessages?.reviewTrigger);
                            return (
                                <div key={line.hccClaimLineNumber} className="claim-detail__line">
                                    <p className="claim-detail__line-title">
                                        Line {line.hccClaimLineNumber}: {line.procedureCode || "—"}
                                    </p>
                                    {reviews.map((review, index) => (
                                        <p key={`r-${index}`} style={{ color: "var(--orange)", marginBottom: ".2rem" }}>
                                            {review?.reviewMessageCode?.messageDescription || review?.messageDescription}
                                        </p>
                                    ))}
                                    {lineExceptions.map((exception, index) => (
                                        <p key={`e-${index}`} className="danger" style={{ marginBottom: ".2rem" }}>
                                            {exception?.exceptionMessageCode?.messageDescription || exception?.exceptionType}
                                        </p>
                                    ))}
                                </div>
                            );
                        })}
                    </>
                ) : (
                    <p className="sub-title">No service lines on this claim.</p>
                )}
            </div>
        </>
    );
};

const ClaimDetails = ({ data }) => {
    const [showRaw, setShowRaw] = useState(false);
    const isRow = data?.details != null;
    const row = isRow ? data : {};
    const details = isRow ? data.details : (data || {});

    const v4Items = asArray(details.items);
    const classicItems = asArray(details.claimDetailsByHccID?.items);
    const primaryV4 = v4Items[0] || {};

    const classicClaim = classicItems[0]?.eobClaimDetail?.claim;
    const claimHccId = row.claimHccId || primaryV4.claimHccId || getClassicHccId(classicItems[0]);
    const claimStatus = row.claimStatus || primaryV4.claimStatus;
    const claimType = row.claimTypeName || primaryV4.claimType || classicClaim?.type;
    const serviceDate = classicClaim?.serviceDateString
        || classicClaim?.startDate
        || primaryV4.header?.dateReceived;
    const inNetwork = classicClaim?.inNetwork;

    if (!claimHccId && !classicItems.length && !v4Items.length) {
        return <p className="sub-title">No claim details found.</p>;
    }

    return (
        <div className="claim-detail">
            <div className="claim-detail__intro">
                <p className="claim-detail__kicker">Claim detail</p>
                <p className="title claim-detail__title">{dash(claimHccId)}</p>
                <p className="claim-detail__lede">
                    {[claimType, serviceDate && `Received ${serviceDate}`].filter(Boolean).join(" · ")}
                </p>
                <div className="claim-detail__badges">
                    {claimStatus && <Badge tone={statusTone(claimStatus)}>{claimStatus}</Badge>}
                    {claimType && <Badge tone="info">{claimType}</Badge>}
                    {inNetwork && (
                        <Badge tone={inNetwork === "Yes" ? "ok" : "warn"}>
                            {inNetwork === "Yes" ? "In network" : "Out of network"}
                        </Badge>
                    )}
                    {row.claimFilingCode && <Badge>{row.claimFilingCode}</Badge>}
                    {row.isVoided === "Y" || primaryV4.voidedClaim ? <Badge tone="bad">Voided</Badge> : null}
                </div>
                <div className="claim-detail__meta">
                    <Meta label="External claim #" value={row.externalClaimNumber} />
                    <Meta label="Member key" value={row.memberKey} />
                    <Meta label="Subscriber" value={row.submittedSubscriberId || primaryV4.header?.submittedSubscriberId} />
                    <Meta label="Payee" value={row.submittedPayeeTypeCode} />
                    <Meta label="Entry time" value={formatDateTime(row.entryTime)} />
                </div>
            </div>

            {classicItems.length > 0 ? (
                classicItems.map((classicItem, index) => {
                    const hccId = getClassicHccId(classicItem) || claimHccId;
                    return (
                        <EobClaimDetailView
                            key={hccId || index}
                            hideIntro
                            hideRawJson
                            record={{
                                ...classicItem,
                                claimHccId: hccId,
                                claimStatus,
                                claimTypeName: claimType,
                            }}
                        />
                    );
                })
            ) : (
                v4Items.map((item) => (
                    <V4Fallback key={item?.claimHccId} item={item} />
                ))
            )}

            <button
                type="button"
                className="clickable-link claim-detail__raw-toggle"
                onClick={() => setShowRaw(!showRaw)}
            >
                {showRaw ? "Hide source JSON" : "Show source JSON"}
            </button>

            {showRaw && (
                <div className="claim-detail__card">
                    <ReactJson
                        src={{ v4: details.items, classic: details.claimDetailsByHccID }}
                        name={false}
                        collapsed={2}
                        collapseStringsAfterLength={150}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        sortKeys
                    />
                </div>
            )}
        </div>
    );
};

export default ClaimDetails;
